import React, {CSSProperties, forwardRef, HTMLAttributes} from 'react'

import Colors from '@utils/Colors.js'

import Icon from '../feedback/Icon.js'
import {ellipsisStyle, getTextColor, textIconGap, typography, TextVariant} from './styles.js'

export interface TextProps extends HTMLAttributes<HTMLDivElement> {
    variant?: TextVariant
    /** One line that ends in ... when it doesn't fit, overflow/whiteSpace/textOverflow in style still win */
    ellipsis?: boolean
    /** Grey text */
    disabled?: boolean
    /** White text, by default when the style has a dark background */
    onDark?: boolean
    /** Icon class (e.g. 'mdi mdi-clock') shown before the text, the text then is a centred flex row */
    icon?: string
}

/** Typography props of the variants (H1..Caption), the same as Text without the variant */
export type TypographyProps = Omit<TextProps, 'variant'>

/**
 * Base of all text components: a <div> with the font of its variant (see typography in styles.ts). The caller's style
 * always wins over the variant, the colour and ellipsis. Rest props and the ref go to the <div>.
 */
const Text = forwardRef<HTMLDivElement, TextProps>(function Text({variant = 'p', style, ellipsis, disabled, onDark, icon, children, ...rest}, ref) {
    const background = typeof style?.background === 'string' ? style.background : undefined

    const textStyle: CSSProperties = {
        ...typography[variant],
        color: getTextColor({variant, disabled, onDark: onDark ?? Colors.isDarkBackground(background)}),
        ...(ellipsis ? ellipsisStyle : {}),
        ...(icon ? {display: 'flex', alignItems: 'center'} : {}),
        ...style
    }

    return (
        <div ref={ref} style={textStyle} {...rest}>
            {icon && <Icon icon={icon} style={{marginRight: textIconGap}} />}
            {children}
        </div>
    )
})

export default Text

/** A text component with a fixed variant (H1..Caption) */
export function createTextVariant(variant: TextVariant, displayName: string) {
    const Variant = forwardRef<HTMLDivElement, TypographyProps>((props, ref) => <Text ref={ref} {...props} variant={variant} />)
    Variant.displayName = displayName
    return Variant
}
